import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { getCardType } from '../../cardTypes/index.js';
import Icon from '../../components/Icon.jsx';
import styles from './CardPreview.module.scss';

function safeRender(def, note, ord) {
  try {
    return def.render(note, ord, { cardId: 'preview', reps: 0 });
  } catch {
    return null;
  }
}

function Face({ view, back }) {
  if (view.mode === 'mcq') {
    return (
      <>
        <div className="rich" dangerouslySetInnerHTML={{ __html: view.front }} />
        <ul className={styles.choices}>
          {view.choices.map((c, i) => (
            <li key={c.id} className={clsx(back && c.correct && styles.right)}>
              <span>{back && c.correct ? <Icon name="check" size={14} stroke={3} /> : String.fromCharCode(65 + i)}</span>
              {c.text}
            </li>
          ))}
        </ul>
        {back && view.back ? <div className={clsx('rich', styles.extra)} dangerouslySetInnerHTML={{ __html: view.back }} /> : null}
      </>
    );
  }
  if (view.mode === 'type') {
    return (
      <>
        <div className="rich" dangerouslySetInnerHTML={{ __html: view.front }} />
        <p className={clsx(styles.typeBox, back && styles.typeAnswer)}>{back ? view.expected || '…' : 'Type your answer'}</p>
        {back && view.back ? <div className={clsx('rich', styles.extra)} dangerouslySetInnerHTML={{ __html: view.back }} /> : null}
      </>
    );
  }
  return <div className="rich" dangerouslySetInnerHTML={{ __html: back ? view.back : view.front }} />;
}

/**
 * The card exactly as it will look while studying. Tap to flip; one tab per card
 * the note makes (e.g. both directions, or one per blank).
 */
export default function CardPreview({ type, fields, className }) {
  const def = getCardType(type);
  const [pickedOrd, setPickedOrd] = useState(null);
  const [back, setBack] = useState(false);

  const ords = useMemo(() => {
    try {
      return def.ords({ fields: fields ?? {} });
    } catch {
      return [];
    }
  }, [def, fields]);

  const ord = ords.includes(pickedOrd) ? pickedOrd : ords[0];
  const view = ord === undefined ? null : safeRender(def, { id: 'preview', type, fields: fields ?? {} }, ord);
  const empty = !view || !(view.front ?? '').replace(/<[^>]*>/g, '').trim();

  return (
    <div className={clsx(styles.preview, className)}>
      {ords.length > 1 ? (
        <div className={styles.tabs} role="tablist" aria-label="Cards this makes">
          {ords.map((o) => (
            <button
              key={o}
              type="button"
              role="tab"
              aria-selected={o === ord}
              className={clsx(styles.tab, o === ord && styles.tabOn)}
              onClick={() => {
                setPickedOrd(o);
                setBack(false);
              }}
            >
              {def.cardLabel(o)}
            </button>
          ))}
        </div>
      ) : null}

      {empty ? (
        <div className={clsx(styles.card, styles.placeholder)}>
          <Icon name={def.icon ?? 'card'} size={28} />
          <p>{type === 'cloze' && ord === undefined ? 'Make a blank to see the card.' : 'Start typing to see your card here.'}</p>
        </div>
      ) : (
        <div
          role="button"
          tabIndex={0}
          className={clsx(styles.card, back && styles.flipped)}
          onClick={() => setBack((b) => !b)}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !e.metaKey && !e.ctrlKey) {
              e.preventDefault();
              e.stopPropagation();
              setBack((b) => !b);
            }
          }}
          aria-label={back ? 'Preview showing the answer. Tap to see the question.' : 'Preview showing the question. Tap to see the answer.'}
        >
          <span className={styles.side}>{back ? 'Answer' : 'Question'}</span>
          <div className={styles.face}>
            <Face view={view} back={back} />
          </div>
          <span className={styles.flipHint}>
            <Icon name="flip" size={16} />
            Tap to {back ? 'see the question' : 'flip'}
          </span>
        </div>
      )}
    </div>
  );
}
